import { inject, Injectable, signal } from '@angular/core';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signIn, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { ProfileHttpService } from '../../features/competition/registration/services/profile-http.service';
import { Router } from '@angular/router';
import { NgZone } from '@angular/core';
import { catchError, filter, firstValueFrom, of, timeout } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private profileHttp = inject(ProfileHttpService);
  private router = inject(Router);
  private zone = inject(NgZone);

  public isLoggedIn = signal(false);
  public userProfile = signal<any | null>(null);
  public isFetchingProfile = signal(false);
  public loginIdentifier = signal<{ type: 'email' | 'phone', value: string } | null>(null)
  public isInitialized = signal(false);

  constructor() {
    this.setupHubListener();
    this.checkCurrentAuth();
  }

  private setupHubListener() {
    Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          this.isLoggedIn.set(true);

          let hasProfile = await this.loadProfile();

          if (!hasProfile) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            hasProfile = await this.loadProfile();
          }

          this.zone.run(() => {
            if (hasProfile) {
              this.router.navigate(['/aguai-2026/registration'], { replaceUrl: true });
            } else {
              this.router.navigate(['/aguai-2026/login'], { queryParams: { error: 'no_profile' }, replaceUrl: true });
              alert('Essa não é uma conta válida do CUJU. Por favor, volte ao app do CUJU e confira seus dados de login.');
            }
          });
          break;

        case 'signedOut':
          this.isLoggedIn.set(false);
          this.userProfile.set(null);
          this.loginIdentifier.set(null);
          break;

        case 'signInWithRedirect_failure':
          this.isLoggedIn.set(false);
          break;

        default:
          break;
      }
    });
  }

  async checkCurrentAuth() {
    try {
      const { tokens } = await fetchAuthSession();

      if (tokens) {
        this.isLoggedIn.set(true);
        const payload = tokens.idToken?.payload;
        const loginValue = (payload?.['email'] || payload?.['phone_number']) as string;

        if (loginValue) {
          this.loginIdentifier.set({
            type: loginValue.includes('@') ? 'email' : 'phone',
            value: loginValue
          });
        }
        await this.loadProfile();
      }
    } catch (err) {
      this.isLoggedIn.set(false);
    } finally {
      this.isInitialized.set(true);
    }
  }

  private async loadProfile(): Promise<boolean> {
    if (this.isFetchingProfile()) return !!this.userProfile();
    this.isFetchingProfile.set(true);

    try {
      const { userId } = await getCurrentUser();

      const session = await fetchAuthSession();
      const idTokenClaims = session.tokens?.idToken?.payload;

      const profileIdentifier = idTokenClaims?.['preferred_username']?.toString() || userId;

      const profile = await firstValueFrom(this.profileHttp.fetchProfile(profileIdentifier));

      if (profile) {
        this.userProfile.set(profile);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      this.isFetchingProfile.set(false);
    }
  }

  async login(username: string, password: string) {
    const result = await signIn({ username, password });
    if (result.isSignedIn) this.isLoggedIn.set(true);
    return result;
  }

  async loginWithSocial(provider: 'Google' | 'Apple') {
    try {
      await signInWithRedirect({ provider });
    } catch (error) {
      console.error('Social login failed', error);
    }
  }

  async logout() {
    try {
      await signOut({ global: false });
      this.isLoggedIn.set(false);
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  }

  getDisplayName() {
    return this.userProfile().displayName;
  }
}


